import type { DeviceType } from '@gc/shared';

/**
 * Which bulk actions each kind of device can actually carry out.
 *
 * This is not cosmetic tidying. Shut down, Restart and Support unlock all
 * travel to a device agent running on Windows, so aiming them at a billiard
 * table or a cafe table has never been able to do anything — the button was a
 * promise the system could not keep. Pause and Resume do work on any session,
 * but the venue bills tables and billiards by the clock without stopping it, so
 * offering them there is noise on the one screen that has to stay scannable.
 *
 * Kept apart from the bar that draws it so the rule is testable in plain node,
 * the same split `deviceMenu.ts` uses for the per-device menu.
 */
/**
 * **`play` and `pause` are not the billing clock.** They are one pair: open a
 * machine so somebody can use the portal — games, apps, the lot — with no
 * account and nothing charged, and close it again. The owner's words: "they use
 * the system, the only difference is it has no billing and no account."
 *
 * `reserve` is the paying door, and it opens the same panel the tile's Reserve
 * button does — a name or an existing tab, a duration, a price.
 */
export type BulkActionId = 'reserve' | 'shutdown' | 'restart' | 'supportUnlock' | 'pause' | 'play';

/** Reserve is the one action every device supports — it is just a sitting. */
const RESERVE_ONLY: readonly BulkActionId[] = ['reserve'];

/** A screen someone plays on, but no agent to power-cycle. */
const TIMED_PLAY: readonly BulkActionId[] = ['reserve', 'play', 'pause'];

const BY_TYPE: Record<DeviceType, readonly BulkActionId[]> = {
  // Only a PC runs the agent, so only a PC can be powered or unlocked.
  PC: ['reserve', 'play', 'pause', 'supportUnlock', 'restart', 'shutdown'],
  CONSOLE: TIMED_PLAY,
  // A cinema room is sold in sittings like a console, and a film can be paused.
  CINEMA: TIMED_PLAY,
  BILLIARD: RESERVE_ONLY,
  TABLE: RESERVE_ONLY,
  ARCADE: RESERVE_ONLY,
  // Unknown hardware: offer only what cannot misfire.
  OTHER: RESERVE_ONLY,
};

export function bulkActionsForType(deviceType: DeviceType): readonly BulkActionId[] {
  return BY_TYPE[deviceType] ?? RESERVE_ONLY;
}

/**
 * Must the employee type the machine count before this action arms?
 *
 * Yes for everything that takes machines away from somebody — power, the
 * support unlock, Pause (which ends sittings). No for the ones that only START
 * something. The exemption used to name `reserve`, from the days Reserve
 * started sittings on the spot; when Play took that job (2026-07-31) the
 * exemption never followed it, so Play asked staff to type "11" before a RED
 * button would arm — which reads as a Play button that does nothing to anyone
 * who does not read the small print (owner, 2026-09-23: "the play function is
 * not working on the PS5's").
 */
export function bulkNeedsTypedConfirm(action: BulkActionId): boolean {
  return action !== 'play' && action !== 'reserve';
}

/**
 * What a MIXED selection may be offered: the intersection, never the union.
 *
 * Selecting ten PCs and one pool table must not offer Shut down — the count on
 * the button says eleven, and a button that quietly means "eleven, except the
 * one it cannot reach" is how staff learn to distrust the screen. An empty
 * selection supports nothing.
 */
export function sharedBulkActions(deviceTypes: readonly DeviceType[]): readonly BulkActionId[] {
  if (deviceTypes.length === 0) return [];

  const [first, ...rest] = deviceTypes;
  let shared = bulkActionsForType(first as DeviceType);
  for (const deviceType of rest) {
    const supported = bulkActionsForType(deviceType);
    shared = shared.filter((action) => supported.includes(action));
  }
  return shared;
}
